/**
 * LOOKING SOMEBODY UP.
 *
 *   "EVERY BIT OF DATA READ AND WRITTEN, ON EVERY PROFILE'S SCREEN, must be
 *    viewable by staff with the necessary permissions."
 *
 * Script 160 is the answer and this file is the wire to it. Every function here
 * is one RPC with two arguments — a code and a reason — because the database
 * writes the log entry BEFORE it reads a row, and refuses the read if the log
 * entry is refused. That means there is no correct way to call any of these
 * without a reason, and no way for this file to forget one.
 *
 * WHY THERE IS NO ReasonGate ON THIS SCREEN
 *
 * The portal's ReasonGate is a modal that logs an action and then hands over to
 * a viewer. It exists for the sections whose viewer is a separate thing. Here
 * the reason IS an argument to the read, so a modal would log the action twice:
 * once from the gate, and once from the function that actually opens the record.
 * Two log lines for one disclosure makes the audit log harder to read, not
 * safer. The reason is collected in the same form as the code and passed down.
 *
 * A CODE, NOT A NAME
 *
 * Prohibition 6: never a person's name or personal code in search. Nothing here
 * searches. You hold the code because the person read it to you off their own
 * screen, and handing it to the database is a lookup rather than a trawl. That
 * is the whole difference, and it is the reason no function in this file takes
 * a name, an email address or a fragment of anything.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });
const unreachable = () => fail('This portal cannot reach the database.');

/**
 * The refusals worth translating.
 *
 * The database says "you do not hold view_billing", which is precise and reads
 * as a system error to somebody who has never seen a permission key. The key is
 * kept — it is what an administrator needs in order to grant it — and a sentence
 * is put around it.
 */
const readable = (message, permission) => {
  const raw = message || '';
  if (/do not hold/i.test(raw)) {
    return `Your account does not hold ${permission}. Somebody in Human Capital can grant it.`;
  }
  if (/needs a captured reason/i.test(raw)) {
    return 'That reason was too short. A few more words — at least eight characters.';
  }
  if (/not an active staff account/i.test(raw)) {
    return 'This account is not an active staff account.';
  }
  if (/no subject on Orchid carries the code/i.test(raw)) {
    return 'No person or business on Orchid carries that code. Check it with them again.';
  }
  if (/export needs a live, time-boxed grant/i.test(raw)) {
    return 'That export needs a live, time-boxed grant naming it. Canon 4.3: being senior is not a standing export right.';
  }
  return raw || 'That record could not be read.';
};

const call = async (fn, args, permission) => {
  if (!isSupabaseConfigured()) return unreachable();
  const { data, error } = await supabase.rpc(fn, args);
  if (error) return fail(readable(error.message, permission));
  return { data: data || [], error: null };
};

/** The header: who this code belongs to. */
export const fetchSubject = (code, reason) =>
  call('fn_staff_subject', { p_code: code, p_reason: reason }, 'view_user_profile_summary');

/** Everything their own screens show, in thirteen sections. */
export const fetchDossier = (code, reason) =>
  call('fn_staff_dossier', { p_code: code, p_reason: reason }, 'view_user_profile_summary');

/** The files themselves. Requires MFA, because view_kyc_document does. */
export const fetchDocumentShelf = (code, reason) =>
  call('fn_staff_document_shelf', { p_code: code, p_reason: reason }, 'view_kyc_document');

/** Why a score is what it is. Not the weights — those are nowhere. */
export const fetchReputationWorkings = (code, reason) =>
  call('fn_staff_reputation_workings', { p_code: code, p_reason: reason }, 'view_trust_events');

/** What the platform recorded happening, in order. */
export const fetchTrustTrail = (code, reason) =>
  call('fn_staff_trust_trail', { p_code: code, p_reason: reason }, 'view_trust_events');

/**
 * Where this subject sits on the trust ladder, and how confident the platform is
 * in the record behind it.
 *
 * Gated on `scores_view`, which is a permission the founder ruled on for the
 * first time on 30 August 2026 — Compliance + Internal Audit, MFA and reason
 * capture. Before that it was named by six RLS policies and existed in no row of
 * the catalogue, so it could be attached to no role and no card: one active
 * staff member in six could read a trust level, and only via 'all'.
 *
 * The function returns the outputs and the two sub-metric percentages and NONE
 * of the five *_points weight columns or the breakdown jsonb. That is deliberate
 * on this side too: the ruling decided who may read a SCORE, and did not publish
 * the calculation to them.
 */
export const fetchTrustStanding = (code, reason) =>
  call('fn_staff_trust_standing', { p_code: code, p_reason: reason }, 'scores_view');

/** The money, in Finance's own terms. */
export const fetchMoneyTrail = (code, reason) =>
  call('fn_staff_money_trail', { p_code: code, p_reason: reason }, 'view_billing');

/** Who holds a seat, and which seats Orchid bills for. */
export const fetchSeatRoster = (code, reason) =>
  call('fn_staff_seat_roster', { p_code: code, p_reason: reason }, 'view_provider_seats');

/** Who at Orchid has looked at this subject, and what reason they gave. */
export const fetchWhoLooked = (code, reason) =>
  call('fn_staff_who_looked', { p_code: code, p_reason: reason }, 'view_admin_activity_log');

/** A job's whole conversation. The highest-cost read there is. */
export const fetchConversation = (jobReference, reason) =>
  call('fn_staff_read_the_conversation',
       { p_job_reference: jobReference, p_reason: reason }, 'resolve_dispute');

/**
 * One job end to end: the quote, the arrival, every price change, what
 * evidence was captured, what was paid and confirmed, and the rating.
 *
 * THE SECOND READ IN THIS PORTAL THAT TAKES A JOB REFERENCE, and until script
 * 170 there was one. Twelve of the thirteen staff reads take p_code -- a
 * person or a business -- because the portal was built around subjects, and a
 * job is not a subject. A customer phoning about their geyser holds a
 * reference and nothing else.
 *
 * view_job_history, NOT resolve_dispute. The conversation read above is gated
 * on dispute authority, and it was the only job-keyed read there was, so
 * letting a first-line agent answer "what happened on my job" meant making
 * them a dispute resolver. Reading what happened is not the same act as
 * deciding who was right about it.
 */
export const fetchJobHistory = (jobReference, reason) =>
  call('fn_staff_job_history',
       { p_job_reference: jobReference, p_reason: reason }, 'view_job_history');

/**
 * Why a business's compliance score is what it is.
 *
 *   "wire it into the Portal so that more businesses don't come complaining to
 *    us about the fact that they aren't scoring higher in that area."
 *
 * Reads the SCORING function's own flags rather than a second list of rules, so
 * the answer a support agent gives on the phone cannot drift from the number
 * the provider is looking at. Gated on view_user_profile_summary, which is the
 * permission for "a customer asked about their own record".
 */
export const fetchComplianceStanding = (code, reason) =>
  call('fn_staff_compliance_standing', { p_code: code, p_reason: reason },
       'view_user_profile_summary');

/** Which profile screen each dossier section stands in for. */
export const fetchCoverage = async () => {
  if (!isSupabaseConfigured()) return unreachable();
  const { data, error } = await supabase.rpc('fn_staff_screens_covered');
  if (error) return fail(error.message || 'The coverage map could not be read.');
  return { data: data || [], error: null };
};

/**
 * The dossier's rows, grouped into its sections, in the database's own order.
 *
 * section_rank rather than alphabetical: the database decided that Identity
 * comes before Money for a reason, and re-sorting here would quietly overrule
 * it the first time somebody added a section.
 */
export function groupSections(rows) {
  const bySection = new Map();
  (rows || []).forEach((row) => {
    if (!bySection.has(row.section)) {
      bySection.set(row.section, { section: row.section, rank: row.section_rank, rows: [] });
    }
    bySection.get(row.section).rows.push(row);
  });
  return [...bySection.values()].sort((a, b) => a.rank - b.rank);
}

/** The eight-character floor the database enforces, said before it refuses. */
export const MIN_REASON = 8;

/** A code, normalised the way the database normalises it. */
export const tidyCode = (code) => (code || '').trim().toUpperCase();

/**
 * Whether a string could be a code at all.
 *
 * Deliberately loose: it rejects a name and an email address, and accepts
 * anything shaped like an Orchid code, so the refusal a person sees for a
 * genuine typo comes from the database — which knows what exists — rather than
 * from a regular expression here, which does not.
 */
export const looksLikeACode = (code) => /^OFX/.test(tidyCode(code));
